import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import ToggleButton from "components/Common/ToggleButton"
import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Label,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useCreateLcDocument, useEditLcDocument } from "hooks/useLcDocuments"

const LcDocumentAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateLcDocument()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditLcDocument()

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter document name"),
    sort_order: Yup.number().nullable().min(0, "Sort order cannot be negative"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name?.trim(),
        description: values.description?.trim() || null,
        is_active: values.is_active,
        is_default: values.is_default,
        is_required: values.is_required,
        sort_order: values.sort_order || 0,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `LC document ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        toast.error("Failed to save LC document.")
      }
    },
    [createMutation, editMutation, editedData, isEdit, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      description: editedData?.description || "",
      is_active: editedData?.is_active ?? true,
      is_default: editedData?.is_default ?? false,
      is_required: editedData?.is_required ?? false,
      sort_order: editedData?.sort_order ?? 0,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const isSubmitDisabled = useMemo(
    () => createLoading || editLoading,
    [createLoading, editLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit LC Document" : "Add LC Document"}
      size="lg"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Card className="border">
          <CardBody>
            <Row>
              <Col md={8}>
                <div className="mb-3">
                  <Label htmlFor="lc-document-name">Document Name</Label>
                  <InputField
                    id="lc-document-name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    invalid={formik.touched.name && !!formik.errors.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="lc-document-sort-order">Sort Order</Label>
                  <InputField
                    id="lc-document-sort-order"
                    name="sort_order"
                    type="number"
                    min="0"
                    value={formik.values.sort_order}
                    onChange={formik.handleChange}
                    invalid={
                      formik.touched.sort_order && !!formik.errors.sort_order
                    }
                  />
                  {formik.touched.sort_order && formik.errors.sort_order && (
                    <FormFeedback type="invalid">
                      {formik.errors.sort_order}
                    </FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
            <div className="mb-3">
              <Label htmlFor="lc-document-description">Description</Label>
              <InputField
                id="lc-document-description"
                name="description"
                type="textarea"
                value={formik.values.description}
                onChange={formik.handleChange}
              />
            </div>
            <Row>
              <Col md={4}>
                <div className="mb-2">
                  <Label className="d-block">Active</Label>
                  <ToggleButton
                    active={formik.values.is_active}
                    onClick={() =>
                      formik.setFieldValue("is_active", !formik.values.is_active)
                    }
                  >
                    {formik.values.is_active ? "Active" : "Inactive"}
                  </ToggleButton>
                  <div className="form-text">
                    Active documents can be selected for vehicle LC docs.
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-2">
                  <Label className="d-block">Default</Label>
                  <ToggleButton
                    active={formik.values.is_default}
                    onClick={() =>
                      formik.setFieldValue("is_default", !formik.values.is_default)
                    }
                  >
                    {formik.values.is_default ? "Default" : "Optional"}
                  </ToggleButton>
                  <div className="form-text">
                    Default documents show automatically when adding vehicle LC docs.
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-2">
                  <Label className="d-block">Required</Label>
                  <ToggleButton
                    active={formik.values.is_required}
                    onClick={() =>
                      formik.setFieldValue("is_required", !formik.values.is_required)
                    }
                  >
                    {formik.values.is_required ? "Required" : "Not Required"}
                  </ToggleButton>
                  <div className="form-text">
                    Required documents are counted as mandatory checklist items.
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {isEdit ? "Save Changes" : "Add Document"}
            {isSubmitDisabled ? "..." : ""}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default LcDocumentAddEdit
